import React, { useEffect } from 'react';
import '@atlaskit/css-reset';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { collectAny, useExperienceContext } from '@atlassian/jira-experience-tracker';
import {
	RedirectToArchivedPage,
	isProjectArchivedCondition,
} from '@atlassian/jira-redirect-archived-project';
import { useRelayResource } from '@atlassian/jira-relay-utils';
import { useResource, useRouter } from '@atlassian/jira-router';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context';
import { codePageResource } from '@atlassian/jira-router-resources-software-code';
import SoftwareAppCodePage, {
	codeErrorBoundaryAttributes,
} from '@atlassian/jira-software-code-page';
import {
	CodePageExperienceStart,
	ViewCodePageExpectedErrorExperienceSuccessTracker,
	VIEW_CODE_MAIN_PAGE_EXPERIENCE_NAME,
	VIEW_CODE_PAGE_EXPERIENCE_NAME,
	VIEW_CONNECTION_PAGE_EXPERIENCE_NAME,
	VIEW_PR_LIST_EXPERIENCE_NAME,
	VIEW_REPO_LIST_EXPERIENCE_NAME,
} from '@atlassian/jira-software-code-page/src/services/experience-tracker';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	ERROR_REPORTING_ID_CLASSIC,
	ERROR_REPORTING_ID_NG,
	ERROR_REPORTING_PACKAGE_NAME,
} from './constants';

export default function CodeSpa() {
	const tenantContext = useTenantContext();
	const experienceTracker = useExperienceContext();
	const projectContextDataResource = useResource(projectContextResource);

	const { queryReference: initialQueryReference } = useRelayResource(codePageResource);

	const [{ match }] = useRouter();
	const projectKey = match.params.projectKey ?? '';

	const hasSoftwareLicense = checkLicense(tenantContext, 'software');
	const errorReportIdPrefix =
		projectContextDataResource.data && projectContextDataResource.data.simplified
			? ERROR_REPORTING_ID_NG
			: ERROR_REPORTING_ID_CLASSIC;

	// Effect to abort experience tracker events when the page is closed
	useEffect(() => {
		const unloadListener = () => {
			const reason = 'Window unloading';
			experienceTracker.abort({
				name: VIEW_CODE_PAGE_EXPERIENCE_NAME,
				reason,
			});
			experienceTracker.abort({
				name: VIEW_REPO_LIST_EXPERIENCE_NAME,
				reason,
			});
			experienceTracker.abort({
				name: VIEW_PR_LIST_EXPERIENCE_NAME,
				reason,
			});
			experienceTracker.abort({
				name: VIEW_CONNECTION_PAGE_EXPERIENCE_NAME,
				reason,
			});
		};
		window.addEventListener('unload', unloadListener);

		return () => {
			window.removeEventListener('unload', unloadListener);
		};
	}, [experienceTracker]);

	if (isProjectArchivedCondition(projectContextDataResource.data)) {
		return <RedirectToArchivedPage />;
	}

	return (
		<>
			<MarkProductStart />
			{initialQueryReference && (
				<AuthenticationCheck>
					<CodePageExperienceStart
						experienceId={`${VIEW_CODE_PAGE_EXPERIENCE_NAME}-${projectKey}`}
						experienceName={VIEW_CODE_PAGE_EXPERIENCE_NAME}
						collect={collectAny([
							VIEW_CODE_MAIN_PAGE_EXPERIENCE_NAME,
							VIEW_CONNECTION_PAGE_EXPERIENCE_NAME,
						])}
					/>
					{hasSoftwareLicense ? (
						<JSErrorBoundary
							id={`${errorReportIdPrefix}-codePage`}
							packageName={ERROR_REPORTING_PACKAGE_NAME}
							sendToPrivacyUnsafeSplunk
							teamName="fusion-isotopes"
							attributes={codeErrorBoundaryAttributes}
							onError={(_, error: Error) => {
								experienceTracker.fail({
									name: VIEW_CODE_PAGE_EXPERIENCE_NAME,
									error,
								});
							}}
						>
							<SubProductUpdater subProduct="software" />
							<SoftwareAppCodePage
								tenantContext={tenantContext}
								initialQueryReference={initialQueryReference}
							/>
						</JSErrorBoundary>
					) : (
						<>
							<AsyncNoSoftwareLicense />
							<ViewCodePageExpectedErrorExperienceSuccessTracker reason="No software license" />
						</>
					)}
				</AuthenticationCheck>
			)}
		</>
	);
}
